import { AxiosError } from "axios"
import { useQueryClient } from "@tanstack/react-query"
import { DialogDisclosure, useDialogState } from "reakit"
import cx from "classnames"

import { ProgressBar } from "../../components/ProgressBar"
import { Toast } from "../../components/Toast"
import HorizontalNumberCard from "../../components/HorizontalNumberCard"
import WizardFooter from "../../components/WizardFooter"
import GovtWizardPracticeStack from "./GovtWizardPracticeStack"
import GovtWizardSelectApproachCard from "./GovtWizardSelectApproachCard"
import GovtWizardHowDoIChooseModal from "./GovtWizardHowDoIChooseModal"
import { ACTION_ICON } from "./constants"
import { LAND_GOALS } from "../../shared/constants"
import { useGovernmentStore } from "../../stores/useGovernmentStore"
import { useAccountId, useUpdateGovtAppWizard } from "../../hooks"
import { formatToShortUSD } from "../../utils"
import { genericErrMsg } from "../../api/auth"
import { GovtApWizardType } from "@/types/govtAppWizard"

export interface GovtWizardContentSelectApproachProps {
  govtAppWizard: GovtApWizardType | undefined
  handlePrevious: () => void
  handleNext: () => void
}

const GovtWizardContentSelectApproach = ({
  govtAppWizard,
  handlePrevious,
  handleNext,
}: GovtWizardContentSelectApproachProps) => {
  const accountId = useAccountId()
  const queryClient = useQueryClient()
  const dialog = useDialogState({ animated: true })

  const { actionKeys, setMultipleActionKeys } = useGovernmentStore()

  const {
    mutateAsync: updateGovtAppWizard,
    isPending: updateGovtAppWizardIsPending,
  } = useUpdateGovtAppWizard(queryClient, accountId, {
    onError: (error: AxiosError) => {
      Toast.error(error?.message || genericErrMsg)
    },
  })

  const handleSave = async (actionKey: string) => {
    const alreadySelected = actionKeys.includes(actionKey)

    const newActionKeys = alreadySelected
      ? actionKeys.filter((key) => key !== actionKey)
      : [...actionKeys, actionKey]

    setMultipleActionKeys(newActionKeys)

    await updateGovtAppWizard({
      wizard_key: "govt_application",
      action_keys: newActionKeys,
    })
  }

  return (
    <>
      <ProgressBar
        className="onboarding-survey-progress-bar w-full h-5"
        value={6}
        max={6}
      />

      <div className="pt-10 lg:pt-13 pb-[162px] sm:pb-31">
        <div className="container max-w-full flex flex-col gap-6 px-5 lg:px-10">
          <div>
            <h1 className="text-3xl text-center font-display font-medium leading-[124%] tracking-[0.6px]">
              Choose practices from our handpicked stacks
            </h1>

            <p className="max-w-[1024px] text-lg text-center leading-[140%] tracking-[0.36px] mx-auto mt-4">
              Choose your favorite practice stacks below, we will keep selected
              stacks in your action plan and saved programs so you don't lose
              them. You will bring these to your meeting with a NRCS
              representative to weigh the pros/cons.{" "}
              <DialogDisclosure
                {...dialog}
                className="link hover:cursor-pointer"
              >
                How do I choose?
              </DialogDisclosure>
            </p>
          </div>
        </div>

        <div className="container max-w-[1440px] mt-7">
          <p className="uppercase text-xs text-center font-bold leading-[140%] tracking-[0.36px]">
            How we chose your stacks:
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-16 mt-[9px]">
            <HorizontalNumberCard
              number={1}
              heading="Your goal"
              text="We start with your land goals to focus on programs that help you accomplish it."
              className={cx(
                "pr-4",
                "relative after:content-['+'] after:absolute after:w-6 after:h-6 after:flex after:items-center after:justify-center after:text-3xl after:font-display after:font-medium after:text-black",
                "after:-bottom-6 lg:after:-bottom-auto lg:after:top-1/2 after:right-1/2 lg:after:-right-11 after:translate-x-1/2 lg:after:-translate-x-0 lg:after:-translate-y-1/2"
              )}
            />

            <HorizontalNumberCard
              number={2}
              heading="Your eligibility"
              text="Programs have rules, and we make sure to show you the ones you fit."
              className={cx(
                "relative after:content-['+'] after:absolute after:w-6 after:h-6 after:flex after:items-center after:justify-center after:text-3xl after:font-display after:font-medium after:text-black",
                "after:-bottom-6 lg:after:-bottom-auto lg:after:top-1/2 after:right-1/2 lg:after:-right-11 after:translate-x-1/2 lg:after:-translate-x-0 lg:after:-translate-y-1/2"
              )}
            />

            <HorizontalNumberCard
              number={3}
              heading="Best suited to your land "
              text="The type of land you have determines the programs you are eligible for."
            />
          </div>
        </div>

        <div className="container max-w-[1512px] mt-[56px]">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {govtAppWizard?.action_groups?.map((actionGroup) => (
              <GovtWizardSelectApproachCard
                key={actionGroup.key}
                heading={LAND_GOALS[actionGroup.goal]}
                potentialEarnings={
                  `${formatToShortUSD(actionGroup.potential_earnings)}/acre` ||
                  "$0/acre"
                }
                impact={actionGroup.impact}
              >
                {actionGroup?.action_data?.map((action) => (
                  <GovtWizardPracticeStack
                    key={action.action_key}
                    icon={
                      ACTION_ICON[action.header as keyof typeof ACTION_ICON]
                    }
                    header={action.header}
                    text={action.text}
                    selected={actionKeys.includes(action.action_key)}
                    onClick={() => {
                      handleSave(action.action_key)
                    }}
                  />
                ))}
              </GovtWizardSelectApproachCard>
            ))}
          </div>
        </div>

        <WizardFooter scrollToBottom={false}>
          <p className="hidden md:flex md:items-center text-overline">
            Your selectionS will save to your Saved programs
          </p>

          <div className="w-full sm:w-auto flex flex-col sm:flex-row gap-4 sm:ml-auto">
            <button
              type="button"
              className="govt-app-page-3-go-back-button btn2 btn2-outline-primary font-bold tracking-[0.32px] whitespace-nowrap"
              onClick={handlePrevious}
            >
              Go Back
            </button>

            <button
              type="button"
              className="govt-app-page-3-next-button btn2 btn2-primary font-bold tracking-[0.32px] whitespace-nowrap"
              disabled={actionKeys.length === 0 || updateGovtAppWizardIsPending}
              onClick={handleNext}
            >
              Next
            </button>
          </div>
        </WizardFooter>
      </div>

      <GovtWizardHowDoIChooseModal dialog={dialog} />
    </>
  )
}

export default GovtWizardContentSelectApproach
